import React, { useState, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import cx from 'classnames';
import { Link } from '@zola/zola-ui/src/components/Link';
import { ButtonV2 } from '@zola/zola-ui/src/components/Button';
import { InputFieldV2 } from '@zola/zola-ui/src/components/Form/InputFieldV2';
import { CheckboxField } from '@zola/zola-ui/src/components/Form/CheckboxField';
import { SegmentControlV2 } from '@zola/zola-ui/src/components/SegmentControlV2';
import { SearchIcon } from '@zola/zola-ui/src/components/SvgIcons';
import useCopyToClipboard from '@zola/zola-ui/src/hooks/useCopyToClipboard';
import { toastsActions } from '@zola-helpers/client/dist/es/redux/toasts';
import { isMobile } from '@zola-helpers/client/dist/es/util/responsive';
import { BUSINESS_UNIT } from '@zola-helpers/client/dist/es/tracking/onboard/onboardTrackingConstants';
import { trackEvent } from '../../../../../util/trackingHelper';
import { hideModal } from '../../../../actions/modalActions';
import styles from './styles.module.less';

interface GuestListInfo {
  id: string;
  envelope_recipient: string;
  email_address: string;
  invited: boolean;
}

interface GuestListInterface {
  guest_groups: GuestListInfo[];
}

type CopyGuestListProps = {
  initialGuestList: GuestListInterface;
};

const segments = [
  {
    key: 'allGuests',
    text: 'All Guests',
  },
  {
    key: 'invitedOnly',
    text: 'Invited Only',
  },
];

const CopyGuestList = ({ initialGuestList }: CopyGuestListProps): JSX.Element => {
  const dispatch = useDispatch();
  const [activeSegment, setActiveSegment] = useState(segments[0].key);
  const [guestList, setGuestList] = useState<Record<string, any[]>>({
    rawGuestList: [],
    rawInvitedGuests: [],
    selectableGuestList: [],
    selectableInvitedGuests: [],
    allSelectableGuestIds: [],
    invitedSelectableGuestIds: [],
  });
  const [selectedGuests, setSelectedGuests] = useState<GuestListInfo[] | any[]>([]);
  const [displayedGuestList, setDisplayedGuestList] = useState<GuestListInfo[] | any[]>([]);
  const [, handleCopy] = useCopyToClipboard();

  useEffect(() => {
    const rawInvitedGuests: any[] = [];
    const allSelectableGuestIds: any[] = [];
    const invitedSelectableGuestIds: any[] = [];
    initialGuestList.guest_groups.map(each => {
      if (each.invited === true) {
        rawInvitedGuests.push(each);
      }
      if (typeof each.email_address === 'string') {
        allSelectableGuestIds.push(each.id);
        if (each.invited === true) {
          invitedSelectableGuestIds.push(each.id);
        }
      }
      return null;
    });
    setGuestList({
      rawGuestList: initialGuestList.guest_groups,
      rawInvitedGuests,
      selectableGuestList: initialGuestList.guest_groups.filter(
        each => typeof each.email_address !== 'string'
      ),
      selectableInvitedGuests: rawInvitedGuests.filter(
        each => typeof each.email_address !== 'string'
      ),
      allSelectableGuestIds,
      invitedSelectableGuestIds,
    });
    setDisplayedGuestList(initialGuestList.guest_groups);
    setSelectedGuests(allSelectableGuestIds);
  }, [initialGuestList]);

  function handleSegmentClick(key: string) {
    setActiveSegment(key);
    if (key === 'allGuests') {
      setDisplayedGuestList(guestList.rawGuestList);
      setSelectedGuests(guestList.allSelectableGuestIds);
    } else {
      setDisplayedGuestList(guestList.rawInvitedGuests);
      setSelectedGuests(guestList.invitedSelectableGuestIds);
    }
  }

  function filterVisibleGuests(inputValue: string) {
    const initialList =
      activeSegment === 'allGuests' ? guestList.rawGuestList : guestList.rawInvitedGuests;
    const newVisibleGuests = initialList.filter(each => {
      return each.envelope_recipient.toLowerCase().indexOf(inputValue.toLowerCase()) > -1;
    });
    setDisplayedGuestList(newVisibleGuests);
  }

  function onCopyEmails() {
    trackEvent('CTA Clicked', {
      business_unit: BUSINESS_UNIT.DIGITAL_SAVE_THE_DATE,
      cta_type: 'BUTTON',
      cta: 'COPY_GUEST_ADDRESSES_MODAL',
    });
    const selectedEmails = selectedGuests.map(guestId => {
      return guestList.rawGuestList.find(each => each.id === guestId).email_address;
    });
    if (selectedEmails.length > 0) {
      handleCopy([...selectedEmails].join(','));
      dispatch(toastsActions.positive({ headline: 'Email addresses copied' }));
      dispatch(hideModal());
    } else {
      dispatch(toastsActions.negative({ headline: 'No emails to copy' }));
    }
  }

  return (
    <div className={cx(styles.container, styles.guestListCopyContainer)}>
      <div className={cx(styles.guestHeader, styles.guestListHeader)}>
        Select email addresses to copy
      </div>
      <div className={cx(styles.content, styles.guestListContent)}>
        <SegmentControlV2
          segments={segments}
          activeSegment={segments.findIndex(({ key }) => key === activeSegment)}
          onClick={handleSegmentClick}
        />
        <div className={styles.allContainer}>
          <CheckboxField
            className={styles.selectAllCheck}
            label="Select all"
            checked={
              activeSegment === 'allGuests'
                ? selectedGuests.length === guestList.allSelectableGuestIds.length
                : selectedGuests.length === guestList.invitedSelectableGuestIds.length
            }
            onChange={() => {
              const selecAllActive =
                activeSegment === 'allGuests'
                  ? selectedGuests.length === guestList.allSelectableGuestIds.length
                  : selectedGuests.length === guestList.invitedSelectableGuestIds.length;
              if (selecAllActive) {
                setSelectedGuests([]);
              } else if (activeSegment === 'allGuests') {
                setSelectedGuests(guestList.allSelectableGuestIds);
              } else {
                setSelectedGuests(guestList.invitedSelectableGuestIds);
              }
            }}
          />
          <SearchIcon
            style={{
              color: '#767676',
              fontSize: '20px',
              marginLeft: '12px',
            }}
          />
          <InputFieldV2
            className={styles.finderInput}
            placeholder="Search"
            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
              filterVisibleGuests(e.target.value)
            }
          />
        </div>
        <div className={styles.guestContainer}>
          {displayedGuestList.length > 0 &&
            displayedGuestList.map((each, i) => (
              <>
                {i !== 0 && <div className={styles.guestDivider} />}
                <div
                  className={styles.guest}
                  data-tooltip={
                    typeof each.email_address !== 'string' ? 'Email not available' : null
                  }
                >
                  <CheckboxField
                    id={each.id}
                    label={each.envelope_recipient}
                    disabled={typeof each.email_address !== 'string'}
                    checked={selectedGuests.indexOf(each.id) >= 0}
                    onChange={() => {
                      const newSelectedGuests = [...selectedGuests];
                      if (selectedGuests.indexOf(each.id) < 0) {
                        newSelectedGuests.push(each.id);
                      } else {
                        const guestIndex = newSelectedGuests.indexOf(each.id);
                        newSelectedGuests.splice(guestIndex, 1);
                      }
                      setSelectedGuests(newSelectedGuests);
                    }}
                  />
                </div>
              </>
            ))}
        </div>
        <Link arrow target="_blank" rel="noopener noreferrer" href="/wedding/manage/guests/all">
          View Guest List
        </Link>
      </div>
      <div className={styles.guestButtonContainer}>
        {isMobile() ? (
          <>
            <ButtonV2 onClick={onCopyEmails} fullWidth={isMobile()}>
              Copy
            </ButtonV2>
            <ButtonV2
              className={styles.cancelbtn}
              variant="secondary"
              onClick={() => dispatch(hideModal())}
              fullWidth={isMobile()}
            >
              Cancel
            </ButtonV2>
          </>
        ) : (
          <>
            <ButtonV2
              className={styles.cancelbtn}
              variant="secondary"
              onClick={() => dispatch(hideModal())}
              fullWidth={isMobile()}
            >
              Cancel
            </ButtonV2>
            <ButtonV2 onClick={onCopyEmails} fullWidth={isMobile()}>
              Copy
            </ButtonV2>
          </>
        )}
      </div>
    </div>
  );
};

export default CopyGuestList;
